import React from 'react';
import styled, { keyframes } from 'styled-components';
import { ArrowRight, Calendar, Store, X } from 'lucide-react';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import { ui } from 'theme/discover';
import EventCardV2 from 'modules/events/card/EventCardV2';
import EstablishmentCardV2 from 'modules/establishment/card/EstablishmentCardV2';
import EventCardSkeleton from 'components/commons/skeletons/EventCardSkeleton';
import { getCategoryIcon } from 'modules/events/categories/categoryIconMap';

// O roxo da home vem da variável do tema (Home.js); nas páginas de busca, o roxo do site.
const ACCENT = `var(--home-accent, ${colors.occasionalPurple})`;

/**
 * O que aparece abaixo da busca do header: as categorias em pílulas, como as
 * do topo da home, e os cartões compactos — os destaques antes de buscar, os
 * resultados depois.
 */
export default function SearchOverlayResults({
    featuredItems,
    results,
    hasSearched,
    isSearching,
    tags,
    activeTag,
    searchTerm,
    isEstablishment,
    onTagClick,
    onCardClick,
    onVerTodos,
    onClose,
}) {
    const noun = isEstablishment ? 'Lugares' : 'Eventos';
    const title = isSearching || hasSearched ? noun : `${noun} em destaque`;

    const items = hasSearched ? results : featuredItems;
    const displayItems = items.slice(0, 8);
    const EmptyIcon = isEstablishment ? Store : Calendar;

    function renderEmpty() {
        if (!hasSearched) {
            return (
                <EmptyState>
                    <EmptyDisc><EmptyIcon size={24} /></EmptyDisc>
                    <EmptyTitle>Nenhum destaque disponível</EmptyTitle>
                </EmptyState>
            );
        }

        return (
            <EmptyState>
                <EmptyDisc><EmptyIcon size={24} /></EmptyDisc>
                <EmptyTitle>
                    Nenhum resultado para{searchTerm ? ` "${searchTerm}"` : ' essa categoria'}
                </EmptyTitle>
                <EmptySubtitle>Tente outros termos ou explore as categorias</EmptySubtitle>
            </EmptyState>
        );
    }

    return (
        <Container>
            <Section>
                <SectionTitle>Categorias</SectionTitle>
                <PillsRow>
                    {tags === null ? (
                        [1, 2, 3, 4, 5, 6].map(i => <PillSkeleton key={i} />)
                    ) : (tags || []).slice(0, 12).map(tag => {
                        const TagIcon = getCategoryIcon(tag?.icon);
                        const selected = activeTag === tag.name;
                        return (
                            <Pill
                                key={tag.id}
                                type='button'
                                $selected={selected}
                                aria-pressed={selected}
                                onClick={() => onTagClick(tag.name)}
                            >
                                <TagIcon size={14} />
                                {tag.name}
                            </Pill>
                        );
                    })}
                </PillsRow>
            </Section>

            <Section>
                <SectionHeader>
                    <SectionTitle>{title}</SectionTitle>
                    <HeaderActions>
                        {!isSearching && displayItems.length > 0 && (
                            <SeeAllLink type='button' onClick={onVerTodos}>
                                Ver tudo
                                <ArrowRight size={14} />
                            </SeeAllLink>
                        )}
                        <CloseButton type='button' onClick={onClose} aria-label='Fechar busca'>
                            <X size={16} />
                        </CloseButton>
                    </HeaderActions>
                </SectionHeader>

                {isSearching ? (
                    <CardsGrid>
                        {[...Array(4)].map((_, i) => <EventCardSkeleton key={i} />)}
                    </CardsGrid>
                ) : displayItems.length === 0 ? (
                    renderEmpty()
                ) : (
                    <CardsGrid>
                        {displayItems.map((item, index) => (
                            <AnimatedCard key={item.slug} $index={index}>
                                {isEstablishment ? (
                                    <EstablishmentCardV2
                                        data={item}
                                        onCardClick={() => onCardClick(item, index)}
                                    />
                                ) : (
                                    <EventCardV2
                                        data={item}
                                        onEventClick={() => onCardClick(item, index)}
                                    />
                                )}
                            </AnimatedCard>
                        ))}
                    </CardsGrid>
                )}
            </Section>
        </Container>
    );
}

/* ===== Layout ===== */

const Container = styled.div`
    width: 100%;
    padding: 4px 16px 24px;
    overflow-x: hidden;

    @media screen and (min-width: ${breakpoints.lg}) {
        max-width: 1100px;
        margin: 0 auto;
        padding: 24px 32px 28px;
        overflow-x: unset;
    }
`;

const Section = styled.section`
    margin-top: 20px;

    &:first-child {
        margin-top: 16px;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        margin-top: 24px;

        &:first-child {
            margin-top: 0;
        }
    }
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;

    h3 {
        margin-bottom: 0;
    }
`;

const SectionTitle = styled.h3`
    margin: 0 0 12px;
    font-size: 18px;
    line-height: 1.3;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: ${ui.ink};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    flex-shrink: 0;
`;

const SeeAllLink = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    font-size: 13.5px;
    font-weight: 700;
    color: ${ACCENT};
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        text-decoration: underline;
    }
`;

const CloseButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 50%;
    color: ${ui.ink};
    background: ${ui.surfaceSunken};
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
        background: ${ui.border};
    }

    &:focus-visible {
        outline: 2px solid ${ACCENT};
        outline-offset: 2px;
    }
`;

/* ===== Categorias ===== */

// Faixa de lado no celular; no desktop as pílulas quebram linha.
const PillsRow = styled.div`
    display: flex;
    gap: 8px;
    width: calc(100% + 32px);
    margin: 0 -16px;
    padding: 0 16px 4px;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-wrap: wrap;
        width: 100%;
        margin: 0;
        padding: 0;
        overflow: visible;
    }
`;

// O desenho das pílulas do redesign (.mpill), as mesmas da home e dos filtros.
const Pill = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    padding: 9px 15px;
    border-radius: 999px;
    border: 1px solid ${({ $selected }) => $selected ? ACCENT : ui.border};
    font: inherit;
    font-size: 13.5px;
    font-weight: ${({ $selected }) => $selected ? 700 : 500};
    line-height: 1.2;
    white-space: nowrap;
    color: ${({ $selected }) => $selected ? colors.neutralLight : ui.ink};
    background: ${({ $selected }) => $selected ? ACCENT : ui.surfaceMuted};
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

    svg {
        color: ${({ $selected }) => $selected ? colors.neutralLight : ACCENT};
    }

    &:hover {
        border-color: ${ACCENT};
    }

    &:focus-visible {
        outline: 2px solid ${ACCENT};
        outline-offset: 2px;
    }
`;

const shimmer = keyframes`
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
`;

const PillSkeleton = styled.div`
    width: 96px;
    height: 37px;
    flex-shrink: 0;
    border-radius: 999px;
    background: ${ui.surfaceSunken};
    animation: ${shimmer} 1.4s infinite;
`;

/* ===== Cartões ===== */

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;

    @media screen and (min-width: ${breakpoints.lg}) {
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 16px;
    }
`;

const fadeInUp = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
`;

const AnimatedCard = styled.div`
    min-width: 0;
    width: 100%;
    animation: ${fadeInUp} 0.3s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: ${({ $index }) => Math.min($index * 35, 180)}ms;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

/* ===== Vazio ===== */

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 36px 16px;
    text-align: center;
`;

const EmptyDisc = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    margin-bottom: 4px;
    border-radius: 50%;
    color: ${ACCENT};
    background: ${ui.tagBg};
`;

const EmptyTitle = styled.p`
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: ${ui.ink};
`;

const EmptySubtitle = styled.p`
    margin: 0;
    font-size: 13px;
    color: ${ui.inkSoft};
`;
